import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Check, Send, Trash2, Loader2, BookOpen, ExternalLink } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const KINDS = ["city", "border", "departure", "arrival", "update"];

const badge = (status) => ({
  draft: "bg-amber-100 text-amber-700",
  approved: "bg-green-100 text-green-700",
}[status] || "bg-charcoal-100 text-charcoal-600");

export default function TripJournalPanel({ tripId }) {
  const [entries, setEntries] = useState([]);
  const [form, setForm] = useState({ name: "", kind: "city" });
  const [busy, setBusy] = useState(false);
  const [drafts, setDrafts] = useState({});

  const load = () => api.get(`/admin/trips/${tripId}/journal`).then((r) => setEntries(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tripId]);

  const create = async () => {
    if (!form.name.trim()) return toast.error("Enter a city or milestone name");
    setBusy(true);
    try {
      await api.post(`/admin/trips/${tripId}/journal`, form);
      toast.success("Draft entry created from verified facts");
      setForm({ name: "", kind: "city" }); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const saveUpdate = async (id) => {
    try { await api.patch(`/admin/journal/${id}`, { real_update: drafts[id] ?? "" }); toast.success("Saved"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const approve = async (id) => { try { await api.post(`/admin/journal/${id}/approve`); toast.success("Approved"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const notify = async (id) => { try { const { data } = await api.post(`/admin/journal/${id}/notify`); toast.success(`Queued ${data.queued}, sent ${data.processed}`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const del = async (id) => { if (!window.confirm("Delete this journal entry?")) return; try { await api.delete(`/admin/journal/${id}`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  return (
    <div className="space-y-4" data-testid="journal-panel">
      <p className="text-xs text-charcoal-500 bg-sand-50 rounded-lg p-3">
        <BookOpen size={13} className="inline mr-1 text-terracotta" />
        Entries are drafted <b>only</b> from a curated, sourced fact table (English + Spanish). Nothing is sent to families until you approve it. Add an optional real update if you like — AI never invents stops, visits or pet details.
      </p>

      <div className="flex gap-2">
        <input data-testid="journal-name" className="border rounded-lg px-3 py-2 text-sm flex-1" placeholder="City / milestone (e.g. Piedras Negras)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <select data-testid="journal-kind" className="border rounded-lg px-2 text-sm" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
          {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
        <button data-testid="journal-add-btn" onClick={create} disabled={busy} className="bg-terracotta text-white rounded-full px-4 text-sm flex items-center gap-1 disabled:opacity-60">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add
        </button>
      </div>

      <ul className="space-y-3">
        {entries.map((e) => (
          <li key={e.id} className="rounded-xl border border-sand-200 bg-white p-4 space-y-2" data-testid={`journal-${e.id}`}>
            <div className="flex items-center gap-2">
              <span className="font-medium text-charcoal-800">{e.name}</span>
              <span className="text-xs text-charcoal-400">{e.state ? `${e.state}, ` : ""}{e.country}</span>
              <span className={`ml-auto text-xs px-2 py-0.5 rounded-full ${badge(e.status)}`}>{e.status}</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-3 text-sm">
              <div><div className="text-[11px] uppercase tracking-wide text-charcoal-400">English</div><div className="text-charcoal-700">{e.body_en}</div></div>
              <div><div className="text-[11px] uppercase tracking-wide text-charcoal-400">Español</div><div className="text-charcoal-700">{e.body_es}</div></div>
            </div>
            {e.fact_source && <a href={e.fact_source} target="_blank" rel="noreferrer" className="text-xs text-terracotta inline-flex items-center gap-1">Source <ExternalLink size={11} /></a>}
            <textarea data-testid={`journal-update-${e.id}`} className="w-full border rounded-lg px-3 py-2 text-sm" rows={2} placeholder="Optional real update (verified by you)…" defaultValue={e.real_update} onChange={(ev) => setDrafts({ ...drafts, [e.id]: ev.target.value })} />
            <div className="flex flex-wrap gap-2 text-xs">
              <button onClick={() => saveUpdate(e.id)} className="border rounded-full px-3 py-1">Save update</button>
              {e.status === "draft" && <button data-testid={`journal-approve-${e.id}`} onClick={() => approve(e.id)} className="bg-green-600 text-white rounded-full px-3 py-1 flex items-center gap-1"><Check size={12} /> Approve</button>}
              {e.status === "approved" && <button data-testid={`journal-notify-${e.id}`} onClick={() => notify(e.id)} className="bg-terracotta text-white rounded-full px-3 py-1 flex items-center gap-1"><Send size={12} /> Notify subscribers</button>}
              <button onClick={() => del(e.id)} className="text-charcoal-400 hover:text-red-600 ml-auto flex items-center gap-1"><Trash2 size={12} /> Delete</button>
            </div>
          </li>
        ))}
        {entries.length === 0 && <li className="text-sm text-charcoal-500">No journal entries yet. Add one when a milestone is reached.</li>}
      </ul>
    </div>
  );
}
